import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheck, FiX, FiSearch, FiUser, FiFileText, FiFilter, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { Button, Checkbox, Input, Pagination, Modal, message, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { apiFetch, apiUrl } from '../api';
import { getVisitStatusBadgeClass, getVisitStatusLabel } from '../utils/visitStatus';
import { isEmployeeRole } from '../utils/companyRoles';
import FilterModal from '../components/FilterModal';
import VisitDetailsModal from '../components/VisitDetailsModal';
import { getLocalDateString } from '../utils/date';

const VisitRequests = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{"role": "admin"}');
  const queryParams = new URLSearchParams(window.location.search);
  const tabFromUrl = queryParams.get('tab');
  const userRole = user.role?.toLowerCase() || '';
  const canCheckInOut = userRole === 'receptionist';
  const isEmployee = isEmployeeRole(userRole);
  const isAdmin = userRole === 'admin';
  const initialTab = tabFromUrl ? tabFromUrl : ((isEmployee || isAdmin) ? 'My Pending' : 'All Requests');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' or 'asc'
  const [requests, setRequests] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalDocs, setTotalDocs] = useState(0);
  const [visitorQuery, setVisitorQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [employees, setEmployees] = useState([]);

  const [previewPhoto, setPreviewPhoto] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [selectedVisitDetails, setSelectedVisitDetails] = useState(null);

  const isApprovedTab = activeTab === 'Approved Visitors' || activeTab === 'Pre-approved visitor';
  const showCheckboxColumn =
    ((isAdmin || isEmployee) && !isApprovedTab) ||
    (canCheckInOut && isApprovedTab) ||
    (isAdmin && activeTab === 'Pre-approved visitor');

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const resp = await apiFetch('/api/employees?all=true');
        const data = await resp.json();
        if (data.success) {
          setEmployees(data.data);
        }
      } catch (e) {
        // ignore
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (isEmployee && employees.length > 0 && !activeFilters.hostId) {
      const myEmp = employees.find(e => e.email === user.email);
      if (myEmp) {
        setActiveFilters(prev => ({ ...prev, hostId: myEmp._id }));
      }
    }
  }, [employees, isEmployee, user.email, activeFilters.hostId]);

  const openPhotoPreview = (photo, name) => {
    if (!photo) return;
    setPreviewPhoto(photo);
    setPreviewName(name);
  };

  const fetchRequests = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        tab: activeTab,
        visitor: visitorQuery,
        sortBy: 'expectedDate',
        sortOrder: sortOrder
      });
      if (activeFilters.status) queryParams.append('status', activeFilters.status);
      if (activeFilters.fromDate) queryParams.append('fromDate', activeFilters.fromDate);
      if (activeFilters.toDate) queryParams.append('toDate', activeFilters.toDate);
      if (activeFilters.hostId) queryParams.append('hostId', activeFilters.hostId);

      const response = await apiFetch(`/api/visits?${queryParams.toString()}`);
      const data = await response.json();
      if (response.ok) {
        setRequests(data.data || []);
        setTotalDocs(data.totalDocs || 0);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setLoading(false);
    }
  }, [page, limit, activeTab, visitorQuery, activeFilters, sortOrder]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, visitorQuery, activeFilters, sortOrder]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchRequests();
  }, [fetchRequests]);

  const handleBulkStatusUpdate = async (status) => {
    if (selectedIds.length === 0) return;
    try {
      const response = await apiFetch('/api/visits/bulk-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds, status: status.toLowerCase(), updatedByRole: user.role })
      });
      if (response.ok) {
        message.success(`Selected requests updated to ${status} successfully!`);
        fetchRequests();
        setSelectedIds([]);
      } else {
        const errData = await response.json().catch(() => ({}));
        message.error(errData.message || 'Failed to update request status.');
      }
    } catch (error) {
      console.error('Error in bulk update:', error);
      message.error('Connection error.');
    }
  };

  const isRequestSelectable = (req) => {
    if (activeTab === 'All Requests' || activeTab === 'My Pending') {
      return req.status === 'pending';
    }
    if (activeTab === 'Pre-approved visitor') {
      return req.status === 'approved' || req.status === 'checked-in';
    }
    return true;
  };

  const toggleSelectAll = () => {
    const selectableRequests = filteredRequests.filter(isRequestSelectable);
    if (selectableRequests.length === 0) return;

    const allSelectableSelected = selectableRequests.every(req => selectedIds.includes(req._id));
    if (allSelectableSelected) {
      setSelectedIds(prev => prev.filter(id => !selectableRequests.some(r => r._id === id)));
    } else {
      setSelectedIds(prev => {
        const newSelection = [...prev];
        selectableRequests.forEach(req => {
          if (!newSelection.includes(req._id)) {
            newSelection.push(req._id);
          }
        });
        return newSelection;
      });
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const getCleanSearchQuery = (query) => {
    const match = query.match(/(VMS-[A-Z0-9]{6})/i);
    return match ? match[1] : query;
  };

  const filteredRequests = requests;

  const requestTabs = ['All Requests', 'My Pending', 'Approved Visitors', 'Pre-approved visitor'];
  const formatVisitDate = (dateValue) => {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return '';

    // If it's a UTC midnight date (representing a date-only value), do not show 5:30 time component
    const isUtcMidnight = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;

    if (isUtcMidnight) {
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    }

    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filterConfig = [
    {
      key: 'fromDate',
      label: 'From Date',
      type: 'date',
      placeholder: 'Select from date'
    },
    {
      key: 'toDate',
      label: 'To Date',
      type: 'date',
      placeholder: 'Select to date'
    },
    ...(!isEmployee ? [{
      key: 'hostId',
      label: 'Host',
      type: 'select',
      showSearch: true,
      options: employees.map(e => ({ label: `${e.firstName} ${e.lastName}`, value: e._id }))
    }] : []),
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { label: 'Pending', value: 'pending' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' }
      ]
    }
  ];

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    setPage(1);
    setShowFilterModal(false);
  };

  const handleResetFilters = () => {
    setActiveFilters({});
    setPage(1);
    setShowFilterModal(false);
  };

  if (!isAdmin && userRole !== 'receptionist' && !isEmployee) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center p-8">
        <div className="w-20 h-20 bg-rose-50 dark:bg-rose-900/20 text-rose-500 rounded-full flex items-center justify-center mb-6">
          <FiX className="w-10 h-10" />
        </div>
        <h2 className="text-3xl font-semibold text-slate-900 dark:text-white mb-2 tracking-tight">Access Denied</h2>
        <p className="text-slate-500 max-w-sm">You do not have permission to view visit requests. Please contact your administrator.</p>
      </div>
    );
  }

  return (
    <div className="pb-10">
      <div className="-mt-4 md:-mt-8 -mx-4 md:-mx-8 px-4 md:px-8 bg-white dark:bg-vms-navy/90 border-b border-slate-100 dark:border-white/10 mb-8 pt-2">
        <div className="flex items-center gap-x-6 overflow-x-auto whitespace-nowrap">
          {requestTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => {
                setActiveTab(tab);
                setSelectedIds([]);
                setActiveFilters({});
                setVisitorQuery('');
              }}
              className={`relative py-3 bg-transparent px-1 text-[13px] transition-colors border-b-2 border-transparent ${activeTab === tab
                  ? 'text-[#03497a] dark:text-[#3290ff] font-semibold border-[#03497a] dark:border-[#3290ff]'
                  : 'text-slate-500 hover:text-[#03497a] dark:text-slate-400 dark:hover:text-[#3290ff] font-normal'
                }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="wm-page">
        <div className="wm-page-header">
          <div>
            <h1 className="wm-page-title dark:text-white">Visit Requests</h1>
            <p className="wm-page-subtitle dark:text-slate-400">Review and manage pre-registration requests.</p>
          </div>

          <AnimatePresence>
            {selectedIds.length > 0 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="wm-card-flat flex items-center gap-3 p-2 pr-6 dark:bg-slate-900"
              >
                <div className="bg-[var(--wm-primary)] text-white px-4 py-2 rounded-md text-xs font-medium">
                  {selectedIds.length} Selected
                </div>
                {isApprovedTab && canCheckInOut && (
                    <>
                      {(activeTab === 'Approved Visitors' || activeTab === 'Pre-approved visitor') && (
                        <Button
                          type="text"
                          icon={<FiCheck />}
                          onClick={() => {
                            const todayStr = getLocalDateString();
                            const invalidVisits = filteredRequests.filter(req =>
                              selectedIds.includes(req._id) &&
                              getLocalDateString(req.expectedDate || req.createdAt) !== todayStr
                            );
                            if (invalidVisits.length > 0) {
                              message.warning(`Check-in is only allowed on the expected visit date. You have selected ${invalidVisits.length} visits not scheduled for today.`);
                              return;
                            }
                            handleBulkStatusUpdate('checked-in');
                          }}
                          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-xl transition-all border-none"
                        >
                          Check In
                        </Button>
                      )}
                      <Button
                        type="text"
                        icon={<FiX />}
                        onClick={() => handleBulkStatusUpdate('checked-out')}
                        className="flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all border-none"
                      >
                        Check Out
                      </Button>
                    </>
                )}
                {isAdmin && activeTab === 'Pre-approved visitor' && selectedIds.length > 0 && (
                  <Button
                    type="text"
                    icon={<FiX />}
                    onClick={() => handleBulkStatusUpdate('rejected')}
                    className="flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all border-none"
                  >
                    Reject
                  </Button>
                )}
                {!isApprovedTab && (
                  <>
                    {showCheckboxColumn && (
                      <>
                        <Button
                          type="text"
                          icon={<FiCheck />}
                          onClick={() => handleBulkStatusUpdate('approved')}
                          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-xl transition-all border-none"
                        >
                          Approve
                        </Button>
                        <Button
                          type="text"
                          icon={<FiX />}
                          onClick={() => handleBulkStatusUpdate('rejected')}
                          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all border-none"
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Filter Controls Row */}
        <div className="wm-card-header bg-[var(--wm-surface-muted)] dark:bg-slate-800/20 flex flex-col md:flex-row gap-4 items-center justify-between rounded-t-xl border border-slate-200 dark:border-slate-800 border-b-0">
          <div className="flex items-center gap-2 max-w-md w-full">
            <Input.Search
              size="large"
              placeholder="Search Visitor Name, Email, Passcode..."
              value={visitorQuery}
              onChange={(e) => setVisitorQuery(e.target.value)}
              onSearch={() => setPage(1)}
              className="w-full custom-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button className="h-10 px-5 font-medium bg-[#e6f4ff] text-[#1677ff] border-transparent hover:bg-[#bae0ff]" onClick={() => setShowFilterModal(true)}>Filter</Button>
            <Button className="h-10 px-5 font-medium bg-slate-50 border-slate-200 text-slate-500 hover:text-slate-700" onClick={handleResetFilters}>Reset</Button>
          </div>
        </div>

        <div className="wm-table-shell dark:bg-slate-900">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-100 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
                  {showCheckboxColumn && (
                    <th className="px-6 py-4 whitespace-nowrap">
                      <Checkbox
                        checked={
                          filteredRequests.filter(isRequestSelectable).length > 0 &&
                          filteredRequests.filter(isRequestSelectable).every(req => selectedIds.includes(req._id))
                        }
                        indeterminate={
                          filteredRequests.filter(isRequestSelectable).some(req => selectedIds.includes(req._id)) &&
                          !filteredRequests.filter(isRequestSelectable).every(req => selectedIds.includes(req._id))
                        }
                        onChange={toggleSelectAll}
                        disabled={filteredRequests.filter(isRequestSelectable).length === 0}
                      />
                    </th>
                  )}
                  <th className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap">Visitor Details</th>
                  <th className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap">Person to Meet & Purpose</th>
                  <th 
                    className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap cursor-pointer select-none hover:bg-slate-200/50 dark:hover:bg-slate-800 transition-colors"
                    onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                  >
                    <div className="flex items-center gap-1.5">
                      Visit Date
                      {sortOrder === 'desc' ? <FiArrowDown className="w-3.5 h-3.5 text-slate-500" /> : <FiArrowUp className="w-3.5 h-3.5 text-slate-500" />}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {loading ? (
                  [...Array(5)].map((_, idx) => (
                    <tr key={idx} className="border-b border-slate-200 dark:border-slate-800">
                      {showCheckboxColumn && (
                        <td className="px-6 py-4">
                          <div className="h-4 w-4 bg-slate-200 dark:bg-slate-800 rounded animate-pulse"></div>
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-7 h-7 rounded-lg bg-slate-200 dark:bg-slate-855 animate-pulse mr-3 shrink-0"></div>
                          <div className="space-y-2 flex-1 min-w-[120px]">
                            <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-3/4"></div>
                            <div className="h-2.5 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-1/2"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-28"></div>
                          <div className="h-2.5 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-20"></div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-24"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-6 bg-slate-200 dark:bg-slate-800 rounded-md animate-pulse w-16"></div>
                      </td>
                    </tr>
                  ))
                ) : filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-8 py-14 text-center text-slate-400 font-medium">
                      No requests found.
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((req) => (
                    <tr 
                      key={req._id} 
                      className={`hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group cursor-pointer ${selectedIds.includes(req._id) ? 'bg-primary-50/30 dark:bg-primary-500/5' : ''}`}
                      onClick={() => setSelectedVisitDetails(req)}
                    >
                      {showCheckboxColumn && (
                        <td className="px-6 py-4">
                          {isRequestSelectable(req) ? (
                            <Checkbox
                              checked={selectedIds.includes(req._id)}
                              onChange={(e) => toggleSelectOne(req._id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">-</span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {req.visitor?.photo ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openPhotoPreview(req.visitor.photo, `${req.visitor?.firstName || 'Unknown'} ${req.visitor?.lastName || 'Visitor'}`);
                              }}
                              className="w-7 h-7 rounded-lg overflow-hidden mr-3 shrink-0 border border-slate-200 dark:border-slate-700"
                              title="View visitor photo"
                            >
                              <img src={req.visitor.photo} alt="Visitor" className="w-full h-full object-cover" />
                            </button>
                          ) : (
                            <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 font-medium text-[10px] mr-3 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/30 group-hover:text-primary-600 transition-all">
                              {req.visitor?.firstName?.charAt(0) || 'V'}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-normal text-slate-800 dark:text-slate-100 truncate">
                              {req.visitor?.firstName || 'Unknown'} {req.visitor?.lastName || 'Visitor'}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 font-normal lowercase truncate leading-tight">
                              {req.visitor?.email?.toLowerCase() || 'no email'}
                            </div>
                            {req.passCode && (
                              <div className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-medium px-1.5 py-0.5 rounded mt-1 inline-block uppercase tracking-wider">
                                Pass: {req.passCode}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-800 dark:text-slate-100 font-normal truncate">
                          {req.host?.firstName || 'N/A'} {req.host?.lastName || ''}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 font-normal leading-tight">{req.purpose || 'General'}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-800 dark:text-slate-100 font-normal whitespace-nowrap">
                        {formatVisitDate(req.expectedDate || req.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-medium normal-case tracking-normal rounded-md ${getVisitStatusBadgeClass(req)}`}>
                          {getVisitStatusLabel(req)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="p-4 flex items-center justify-end">
          <Pagination
            current={page}
            pageSize={limit}
            total={totalDocs}
            showSizeChanger
            pageSizeOptions={[10, 25, 50, 100]}
            onChange={(p, ps) => { setPage(ps !== limit ? 1 : p); setLimit(ps); }}
          />
        </div>
      </div>
      <VisitDetailsModal
        open={Boolean(selectedVisitDetails)}
        onCancel={() => setSelectedVisitDetails(null)}
        visit={selectedVisitDetails}
      />
      <Modal
        open={Boolean(previewPhoto)}
        onCancel={() => setPreviewPhoto('')}
        footer={null}
        title={previewName || 'Visitor Photo'}
        width="min(100%, 32rem)"
        styles={{ body: { padding: '0.75rem 1rem 1rem' } }}
      >
        <div className="flex justify-center">
          <img
            src={previewPhoto}
            alt={previewName || 'Visitor'}
            className="w-full max-h-[70dvh] object-contain rounded-lg border border-slate-200 dark:border-slate-700"
          />
        </div>
      </Modal>
      <FilterModal
        open={showFilterModal}
        onCancel={() => setShowFilterModal(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        config={filterConfig}
        initialValues={activeFilters}
      />
    </div>
  );
};

export default VisitRequests;
